import { Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map, Observable } from 'rxjs';
import { deepEqual, deepCopy } from 'src/app/helpers/transformation-utils';

@Injectable()
export abstract class BaseDataService<T extends object> {
  protected state$: BehaviorSubject<T>;
  private readonly initialState: T;

  constructor(initialState: T) {
    this.initialState = deepCopy(initialState);
    this.state$ = new BehaviorSubject<T>(deepCopy(this.initialState));
  }

  /**
   * Updates total or part of the state by merging the provided partial state with the current state.
   * @param partialState A partial state object to merge.
   */
  updateState(partialState: Partial<T>): void {
    const currentState = deepCopy(this.state$.getValue());
    this.state$.next({ ...currentState, ...deepCopy(partialState) });
  }

  /**
   * Clears the state by emitting the initial state.
   * @param initialState The initial state to reset to.
   */
  clearState(): void {
    this.state$.next(deepCopy(this.initialState));
  }

  /**
   * Returns the state as an Observable. Components can subscribe to this to receive state updates.
   * @returns An Observable of the state.
   */
  getState$(): Observable<T> {
    return this.state$.asObservable().pipe(
      map((state) => deepCopy(state)),
      distinctUntilChanged((prev, curr) => deepEqual(prev, curr))
    );
  }

  /**
   * Returns the current value of the state.
   * @returns The current state value.
   */
  getStateValue(): T {
    return deepCopy(this.state$.getValue());
  }

  /**
   * Returns the value of an especific property of the state.
   * @param propertyName The name of the property to get the value.
   * @returns The value of the specified property.
   */
  getStatePropValue<K extends keyof T>(propertyName: K): T[K] {
    return deepCopy(this.getStateValue()[propertyName]);
  }

  /**
   * Returns an Observable that emits only when the specified property of the state changes.
   * @param propertyName The name of the property to listen to.
   * @returns An Observable of the specified property's value.
   */
  select$<K extends keyof T>(propertyName: K): Observable<T[K]> {
    return this.state$.asObservable().pipe(
      map((state) => deepCopy(state[propertyName])),
      distinctUntilChanged((prev, curr) => deepEqual(prev, curr))
    );
  }
}
